import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { MenuSeatGate, MenuWrapper } from '../../components'
import { getPublicMenu } from '../../utilities/api'
import { appendEventQueryParam } from '../../utilities/fnbMenu'

function getVisitStorageKey(organizationUuid, menuKey, venueUuid, eventUuid) {
  return `menu-seat:${organizationUuid}:${venueUuid || '~'}:${eventUuid || '~'}:${menuKey}`
}

function decodeMenuParam(value) {
  if (!value) return ''
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function buildMenuPath(
  organizationUuid,
  menuKey,
  venueUuid,
  searchParams,
  eventUuid
) {
  const params = appendEventQueryParam(searchParams, eventUuid)
  const query = params.toString()
  const suffix = query ? `?${query}` : ''
  const encodedMenuKey = encodeURIComponent(menuKey)

  if (venueUuid) {
    return `/menu/${organizationUuid}/${encodeURIComponent(venueUuid)}/${encodedMenuKey}${suffix}`
  }

  return `/menu/${organizationUuid}/${encodedMenuKey}${suffix}`
}

export default function MenuPage() {
  const navigate = useNavigate()
  const { organization_uuid, venue_uuid, section_name } = useParams()
  const [searchParams] = useSearchParams()

  const menuKey = section_name ?? ''
  const venueUuid = venue_uuid ? decodeMenuParam(venue_uuid) : ''
  const eventUuid =
    searchParams.get('event')?.trim() ||
    searchParams.get('eventUuid')?.trim() ||
    ''
  const menuKeyLabel = decodeMenuParam(menuKey)
  const rowFromQuery = searchParams.get('row')?.trim() ?? ''
  const seatFromQuery = searchParams.get('seat')?.trim() ?? ''
  const orderFromQuery = searchParams.get('order')?.trim() ?? ''

  const [rowName, setRowName] = useState(rowFromQuery)
  const [seatName, setSeatName] = useState(seatFromQuery)
  const [showGate, setShowGate] = useState(false)
  const [ready, setReady] = useState(false)
  const [menuAccess, setMenuAccess] = useState(null)

  useEffect(() => {
    if (!organization_uuid || !menuKey) return

    let cancelled = false

    getPublicMenu(organization_uuid, menuKey, '-', '-', {
      venueUuid,
      eventUuid,
    })
      .then((res) => {
        if (cancelled) return
        const data = res?.data
        setMenuAccess({
          accessMode: data?.accessMode || 'seat_delivery',
          sectionLabel: data?.seat?.section || menuKeyLabel,
          locationName: data?.location?.name || null,
        })
      })
      .catch(() => {
        if (!cancelled) {
          setMenuAccess({
            accessMode: 'seat_delivery',
            sectionLabel: menuKeyLabel,
            locationName: null,
          })
        }
      })

    return () => {
      cancelled = true
    }
  }, [organization_uuid, menuKey, menuKeyLabel, venueUuid, eventUuid])

  useEffect(() => {
    if (!menuAccess || !organization_uuid || !menuKey) return

    if (menuAccess.accessMode === 'pickup') {
      setRowName('')
      setSeatName('')
      setShowGate(false)
      setReady(true)
      return
    }

    const hasRow = Boolean(rowFromQuery)
    const hasSeat = Boolean(seatFromQuery)

    if (hasRow && hasSeat) {
      setRowName(rowFromQuery)
      setSeatName(seatFromQuery)
      setShowGate(false)
      setReady(true)
      return
    }

    if (hasRow || hasSeat) {
      setRowName(rowFromQuery)
      setSeatName(seatFromQuery)
      setShowGate(true)
      setReady(true)
      return
    }

    const storageKey = getVisitStorageKey(
      organization_uuid,
      menuKey,
      venueUuid,
      eventUuid
    )
    const remembered = sessionStorage.getItem(storageKey)

    if (remembered) {
      try {
        const { row, seat } = JSON.parse(remembered)
        if (row && seat) {
          const nextParams = appendEventQueryParam(
            new URLSearchParams({ row, seat }),
            eventUuid
          )
          navigate(
            buildMenuPath(
              organization_uuid,
              menuKey,
              venueUuid,
              nextParams,
              eventUuid
            ),
            { replace: true }
          )
          return
        }
      } catch {
        sessionStorage.removeItem(storageKey)
      }
    }

    setShowGate(true)
    setReady(true)
  }, [
    menuAccess,
    organization_uuid,
    menuKey,
    venueUuid,
    eventUuid,
    rowFromQuery,
    seatFromQuery,
    navigate,
  ])

  const handleGateContinue = ({ row, seat, remember }) => {
    const trimmedRow = row.trim()
    const trimmedSeat = seat.trim()
    if (!trimmedRow || !trimmedSeat) return

    if (remember) {
      sessionStorage.setItem(
        getVisitStorageKey(organization_uuid, menuKey, venueUuid, eventUuid),
        JSON.stringify({ row: trimmedRow, seat: trimmedSeat })
      )
    }

    const nextParams = appendEventQueryParam(
      new URLSearchParams({
        row: trimmedRow,
        seat: trimmedSeat,
      }),
      eventUuid
    )
    navigate(
      buildMenuPath(
        organization_uuid,
        menuKey,
        venueUuid,
        nextParams,
        eventUuid
      )
    )
  }

  const isPickup = menuAccess?.accessMode === 'pickup'
  const canShowMenu = isPickup || Boolean(rowName && seatName)

  return (
    <div className="full-height-wrapper">
      {!ready && <div className="menu-seat-gate--checking" />}
      {ready && showGate && (
        <MenuSeatGate
          sectionName={menuAccess?.sectionLabel || menuKeyLabel}
          initialRow={rowName}
          initialSeat={seatName}
          onContinue={handleGateContinue}
        />
      )}
      {ready && canShowMenu && !showGate && (
        <MenuWrapper
          organizationUuid={organization_uuid}
          menuKey={menuKey}
          venueUuid={venueUuid}
          eventUuid={eventUuid}
          rowName={rowName}
          seatName={seatName}
          fulfillmentType={menuAccess?.accessMode || 'seat_delivery'}
          locationName={menuAccess?.locationName || ''}
          initialOrderNumber={orderFromQuery}
        />
      )}
    </div>
  )
}
